import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Breadcrumb } from 'react-breadcrumbs'
import { withRouter } from 'react-router-dom'
import { authorization } from 'Module/Auth/Auth'
import PinUserIcon from 'Assets/Images/icons/pinUser'
import { getCityLink, getAmbassadorsLink } from 'Utils/Link'
import { futureMissionsCount } from 'Utils/Filters'
import { PATHS } from 'Config/Routing/paths'
import { HideWhenBlocked } from 'Containers/UIBlock/HideWhenBlocked'
import { AmbassadorWrapper } from 'Components/City/Page/Ambassador/AmbassadorWrapper'
import { Breadcrumbs } from 'Components/Breadcrumbs'
import { PageTitle } from 'Components/PageTitle'
import * as CONST from 'Reducers/Ambassador/const'
import {
  getAmbassadorByIdGraphQL,
  getAmbassadorDocumentsById,
  getAmbassadorActivitiesById,
  updateAmbassadorById,
  ressetAmbassadorData,
} from './../../../../../Reducers/Ambassador/actions'
import { getCities } from 'Reducers/City/actions'
import { getClients } from 'Reducers/Clients/actions'
import { apiClient } from '../../../../../Module/API/Client'
import MerchandiseSection from '../Components/MerchandiseSection/container/MerchandiseSection'
import SettingsSection from '../Components/SettingsSection/container/SettingsSection'
import { getBAName } from '../../../../../Utils/User'
import LegalDocumentsSection from '../Components/LegalDocumentsSection/container/LegalDocumentsSection'
import ContactDetailsSection from '../Components/ContactDetailsSection'
import IdentificationFilesSection from '../Components/IdentificationFilesSection'
import AmbassadorStatisticsSection from '../Components/StatisticsSection'
import ActivitySection from '../Components/ActivitySection'
import BankDetailsSection from '../Components/BankDetailsSection'
import { updateAmbassadorByIdGraphQL } from '../../../../../Reducers/Ambassador/actions'

const Ambassador = props => {
  const { match } = props
  const {
    url,
    params: { ambassadorId },
  } = match
  const dispatch = useDispatch()
  const [isFadeLoading, setIsFadeLoading] = useState(false)
  const [fade, setFade] = useState(null)

  const ambassador = useSelector(state => state.ambassadors.ambassadorById)
  const ambassadorDocuments = useSelector(
    state => state.ambassadors.ambassadorDocumentsById,
  )
  const activitiesSortBy = useSelector(
    state => state.ambassadors.activitiesSortBy,
  )
  const activityStatus = useSelector(state => state.ambassadors.activityStatus)
  const cities = useSelector(state => state.city.citiesList)

  useEffect(() => {
    dispatch(getCities(true))
    dispatch(getClients())
    dispatch(getAmbassadorByIdGraphQL(ambassadorId))
    dispatch(getAmbassadorDocumentsById(ambassadorId))
    return () => dispatch(ressetAmbassadorData())
  }, [])

  useEffect(() => {
    loadFadeValue()
  }, [ambassadorId])

  const {
    id,
    city,
    firstName,
    lastName,
    status,
    isNewcomer,
    isTeamLeader,
    missions,
  } = ambassador

  const teamLeaderEnabled = city && city.teamLeaderEnabled

  const userName = getBAName({
    firstName,
    lastName,
    isTeamLeader,
  })
  const cityPathname = getCityLink(city)
  const ambassadorsPathname = getAmbassadorsLink(ambassador)

  const filterActivitiesByStatus = activities => {
    if (activityStatus) {
      return activities.filter(item => {
        return !activityStatus || activityStatus.indexOf(item.status) > -1
      })
    }
    return activities
  }

  const toggleMissionsLimit = () => {
    dispatch(
      updateAmbassadorById(ambassadorId, {
        extended_missions_limit: !ambassador.extendedMissionsLimit,
      }),
    )
  }

  async function handleUserActions({ status, blockedReason, unbookMissions }) {
    await dispatch(
      updateAmbassadorByIdGraphQL(ambassadorId, {
        statusV2: status,
        blockedReason,
        unbookMissions,
      }),
    )
    if (unbookMissions)
      await dispatch(getAmbassadorActivitiesById(ambassadorId))
  }

  async function loadFadeValue() {
    if (!authorization.isAdmin() && !authorization.isCityManager()) return

    setIsFadeLoading(true)
    apiClient
      .getFadeValue(ambassadorId)
      .then(result => {
        setIsFadeLoading(false)
        setFade(result.fade)
      })
      .catch(err => {
        console.error(err)
        setIsFadeLoading(false)
      })
  }

  async function onFadeChange() {
    const shouldFade = !fade

    setIsFadeLoading(true)
    apiClient
      .setFadeValue(ambassadorId, shouldFade)
      .then(result => {
        setIsFadeLoading(false)
        setFade(result.fade)
      })
      .catch(err => {
        console.error(err)
        setIsFadeLoading(false)
      })
  }

  const statusHTML = useMemo(() => {
    var statusID = status

    if (status == 'blocked' && futureMissionsCount(missions)) {
      statusID = 'terminating'
    }

    return (
      status && (
        <span className="status">
          ({CONST.AMBASSADOR_STATUS_LABEL[statusID]})
        </span>
      )
    )
  }, [ambassadorId, status])

  if (!id) return null

  return (
    <HideWhenBlocked>
      <AmbassadorWrapper>
        <Breadcrumbs icon={<PinUserIcon style={{ width: 16 }} />} />
        <Breadcrumb
          data={{ title: 'Dashboard', pathname: `${PATHS.main.dashboard}` }}
        />
        <Breadcrumb
          data={{
            title: 'Cities',
            pathname: `${PATHS.main.dashboard}${PATHS.dashboard.cities}`,
          }}
        />
        <Breadcrumb data={{ title: city.name, pathname: cityPathname }} />
        <Breadcrumb
          data={{ title: 'Brand Ambassadors', pathname: ambassadorsPathname }}
        />
        <Breadcrumb
          data={{
            title: firstName + ' ' + lastName,
            pathname: url,
          }}
        />
        <PageTitle>
          {userName}
          {statusHTML}
          {isNewcomer && (
            <div
              style={{ float: 'right', lineHeight: '34px', fontSize: '16px' }}
            >
              <span role="img" aria-label="warning">
                ⚠️
              </span>
              Newcomer
              <span role="img" aria-label="warning">
                ⚠️
              </span>
            </div>
          )}
        </PageTitle>
        <div>
          <ContactDetailsSection
            ambassadorId={ambassadorId}
            ambassador={ambassador}
            cities={cities}
          />
          <IdentificationFilesSection
            ambassador={ambassador}
            ambassadorDocuments={ambassadorDocuments}
            cities={cities}
            onFadeChange={onFadeChange}
            fade={fade}
            isFadeLoading={isFadeLoading}
            handleUserActions={handleUserActions}
          />
          <SettingsSection
            ambassador={ambassador}
            teamLeaderEnabled={teamLeaderEnabled}
          />
          <MerchandiseSection ambassadorId={ambassadorId} />
          <AmbassadorStatisticsSection
            ambassador={ambassador}
            toggleMissionsLimit={toggleMissionsLimit}
          />
          <BankDetailsSection ambassador={ambassador} />
          <ActivitySection
            ambassadorId={ambassadorId}
            match={match}
            filterActivitiesByStatus={filterActivitiesByStatus}
            activitiesSortBy={activitiesSortBy}
          />
          <LegalDocumentsSection
            signedLegalDocuments={ambassador.signedLegalDocuments}
            formW9={ambassador.formW9}
          />
        </div>
      </AmbassadorWrapper>
    </HideWhenBlocked>
  )
}

export default withRouter(Ambassador)



// WEBPACK FOOTER //
// ./src/Containers/City/Page/Ambasador/View/index.js